import { useState } from "react";
import { Check, X, PencilSimple, CircleNotch, Flask, ArrowRight } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import type { ProposedRecord, ProposedRecordStatus, ProposedLabRecord } from "@researcher/shared";
import { apiUrl } from "../../config/api";
import { useAuth } from "../../contexts/AuthContext";

interface ProposedRecordsCardProps {
  summary: string;
  sourceDescription: string;
  records: ProposedRecord[];
  /** Local statuses persisted on the message — survives re-renders. */
  statuses?: Record<string, ProposedRecordStatus>;
  onStatusChange?: (id: string, status: ProposedRecordStatus) => void;
  /** Persist data edits back to the message so they survive a re-save. */
  onDataChange?: (id: string, data: ProposedLabRecord) => void;
}

const LAB_CATEGORIES: ProposedLabRecord['category'][] = [
  'hormonal', 'thyroid', 'metabolic', 'vitamin',
  'blood_count', 'inflammatory', 'fertility', 'other',
];

/**
 * Inline confirmation card rendered on an assistant message when Romy
 * proposes structured records from an uploaded image/PDF. Each row shows the
 * extracted value with Save / Edit / Discard buttons. Nothing writes to the
 * user's profile until they hit Save.
 */
export default function ProposedRecordsCard({
  summary,
  sourceDescription,
  records,
  statuses = {},
  onStatusChange,
  onDataChange,
}: ProposedRecordsCardProps) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [savingAll, setSavingAll] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recordsToReview = records.filter((r) => (statuses[r.id] ?? 'pending') === 'pending');
  const savedCount = records.filter((r) => statuses[r.id] === 'saved').length;
  const remaining = recordsToReview.length;

  async function saveRecords(toSave: ProposedRecord[]) {
    if (toSave.length === 0 || !currentUser) return;
    setError(null);
    setSavingAll(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(apiUrl('/chat/confirm-extraction'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sourceDescription,
          records: toSave.map((r) => ({
            id: r.id,
            collection: r.collection,
            data: r.data,
          })),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || 'Save failed');
      }
      const body = await res.json();
      const savedIds = new Set<string>(
        (body?.data?.saved || []).map((s: any) => s.proposalId),
      );
      const failedIds = new Set<string>(
        (body?.data?.errors || []).map((e: any) => e.proposalId),
      );
      for (const r of toSave) {
        if (savedIds.has(r.id)) {
          onStatusChange?.(r.id, 'saved');
        } else if (failedIds.has(r.id)) {
          onStatusChange?.(r.id, 'failed');
        }
      }
      if (failedIds.size > 0) {
        setError(`${failedIds.size} record${failedIds.size === 1 ? '' : 's'} failed to save.`);
      }
    } catch (err: any) {
      console.error('[ProposedRecordsCard] Save failed:', err);
      setError(err.message || 'Could not save. Please try again.');
    } finally {
      setSavingAll(false);
    }
  }

  return (
    <div className="mt-3 border border-pink-200 dark:border-pink-800 rounded-xl bg-gradient-to-br from-pink-50/50 to-orange-50/50 dark:from-pink-900/10 dark:to-orange-900/10 overflow-hidden">
      <div className="px-4 py-3 border-b border-pink-200/60 dark:border-pink-800/60 flex items-start gap-2">
        <Flask size={18} weight="fill" className="text-pink-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
            {summary}
          </p>
          {sourceDescription && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {sourceDescription}
            </p>
          )}
        </div>
      </div>

      <div className="divide-y divide-pink-100 dark:divide-pink-900/40">
        {records.map((rec) => {
          const status = statuses[rec.id] ?? 'pending';
          const isEditing = editingId === rec.id;
          return (
            <RecordRow
              key={rec.id}
              record={rec}
              status={status}
              isEditing={isEditing}
              onEditToggle={() => setEditingId(isEditing ? null : rec.id)}
              onSave={(updated) => {
                if (updated) onDataChange?.(rec.id, updated);
                setEditingId(null);
                saveRecords([{ ...rec, data: updated ?? rec.data }]);
              }}
              onDiscard={() => onStatusChange?.(rec.id, 'discarded')}
            />
          );
        })}
      </div>

      {error && (
        <div className="px-4 py-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/10 border-t border-red-200 dark:border-red-800">
          {error}
        </div>
      )}

      {remaining > 1 && (
        <div className="px-4 py-3 flex items-center justify-between gap-2 border-t border-pink-200/60 dark:border-pink-800/60">
          <button
            type="button"
            onClick={() => saveRecords(recordsToReview)}
            disabled={savingAll}
            className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-orange-500 to-pink-500 text-white text-sm font-medium hover:from-orange-600 hover:to-pink-600 transition-all disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
          >
            {savingAll
              ? <><CircleNotch size={14} className="animate-spin" />Saving…</>
              : <><Check size={14} weight="bold" />Save all {remaining}</>}
          </button>
          <button
            type="button"
            onClick={() => recordsToReview.forEach((r) => onStatusChange?.(r.id, 'discarded'))}
            disabled={savingAll}
            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-60"
          >
            Discard all
          </button>
        </div>
      )}

      {savedCount > 0 && (
        <div className="px-4 py-2.5 text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-t border-emerald-200 dark:border-emerald-800 flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5">
            <Check size={12} weight="bold" />
            {savedCount} record{savedCount === 1 ? '' : 's'} saved to your profile
          </span>
          <button
            type="button"
            onClick={() => navigate('/health/vitals')}
            className="inline-flex items-center gap-0.5 text-emerald-700 dark:text-emerald-400 hover:text-emerald-900 dark:hover:text-emerald-200 font-medium"
          >
            View in Vitals
            <ArrowRight size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

function RecordRow({
  record,
  status,
  isEditing,
  onEditToggle,
  onSave,
  onDiscard,
}: {
  record: ProposedRecord;
  status: ProposedRecordStatus;
  isEditing: boolean;
  onEditToggle: () => void;
  onSave: (updated: ProposedLabRecord | null) => void;
  onDiscard: () => void;
}) {
  const [draft, setDraft] = useState<ProposedLabRecord>(record.data);
  const [savingRow, setSavingRow] = useState(false);

  const handleSaveSingle = async () => {
    setSavingRow(true);
    try {
      await onSave(isEditing ? draft : null);
    } finally {
      setSavingRow(false);
    }
  };

  if (status === 'saved') {
    return (
      <div className="px-4 py-2.5 flex items-center gap-2 opacity-70">
        <Check size={14} weight="bold" className="text-emerald-500 flex-shrink-0" />
        <span className="text-sm text-gray-600 dark:text-gray-400 line-through">
          {record.summary}
        </span>
      </div>
    );
  }
  if (status === 'discarded') {
    return (
      <div className="px-4 py-2.5 flex items-center gap-2 opacity-50">
        <X size={14} weight="bold" className="text-gray-400 flex-shrink-0" />
        <span className="text-sm text-gray-500 dark:text-gray-400 line-through">
          {record.summary}
        </span>
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div className="px-4 py-2.5 flex items-center justify-between gap-2 bg-red-50/50 dark:bg-red-900/10">
        <span className="text-sm text-red-700 dark:text-red-400">
          Failed: {record.summary}
        </span>
        <button
          type="button"
          onClick={handleSaveSingle}
          className="text-xs text-red-700 dark:text-red-400 font-medium underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 py-3">
      {!isEditing ? (
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
              {record.summary}
            </p>
            {record.confidence < 0.8 && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">
                Lower confidence — please double-check before saving
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={onEditToggle}
              disabled={savingRow}
              className="p-1.5 rounded-md text-gray-500 hover:text-pink-500 hover:bg-pink-50 dark:hover:bg-pink-900/20 transition-colors"
              aria-label="Edit record"
              title="Edit"
            >
              <PencilSimple size={14} weight="bold" />
            </button>
            <button
              type="button"
              onClick={onDiscard}
              disabled={savingRow}
              className="p-1.5 rounded-md text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              aria-label="Discard record"
              title="Discard"
            >
              <X size={14} weight="bold" />
            </button>
            <button
              type="button"
              onClick={handleSaveSingle}
              disabled={savingRow}
              className="p-1.5 rounded-md text-white bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 transition-colors disabled:opacity-60"
              aria-label="Save record"
              title="Save"
            >
              {savingRow
                ? <CircleNotch size={14} className="animate-spin" />
                : <Check size={14} weight="bold" />}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Test">
              <input
                type="text"
                value={draft.testName}
                onChange={(e) => setDraft({ ...draft, testName: e.target.value })}
                className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-gray-800 dark:text-gray-100"
              />
            </Field>
            <Field label="Date">
              <input
                type="date"
                value={draft.date}
                onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-gray-800 dark:text-gray-100"
              />
            </Field>
            <Field label="Value">
              <input
                type="number"
                step="any"
                value={draft.value}
                onChange={(e) => setDraft({ ...draft, value: Number(e.target.value) })}
                className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-gray-800 dark:text-gray-100"
              />
            </Field>
            <Field label="Unit">
              <input
                type="text"
                value={draft.unit}
                onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
                className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-gray-800 dark:text-gray-100"
              />
            </Field>
            <Field label="Category">
              <select
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value as ProposedLabRecord['category'] })}
                className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-gray-800 dark:text-gray-100"
              >
                {LAB_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c.replace('_', ' ')}</option>
                ))}
              </select>
            </Field>
            <Field label="Flag">
              <select
                value={draft.flagged ?? ''}
                onChange={(e) => setDraft({ ...draft, flagged: (e.target.value || null) as ProposedLabRecord['flagged'] })}
                className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-gray-800 dark:text-gray-100"
              >
                <option value="">none</option>
                <option value="normal">normal</option>
                <option value="high">high</option>
                <option value="low">low</option>
              </select>
            </Field>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onEditToggle}
              className="px-2 py-1 rounded-md text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveSingle}
              disabled={savingRow}
              className="px-3 py-1 rounded-md text-xs text-white bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 disabled:opacity-60 inline-flex items-center gap-1"
            >
              {savingRow ? <CircleNotch size={12} className="animate-spin" /> : <Check size={12} weight="bold" />}
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">
      {label}
      <div className="mt-0.5 normal-case">{children}</div>
    </label>
  );
}
